// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { withTranslation, WithTranslation } from "react-i18next";
import { initializeIcons } from "office-ui-fabric-react/lib/Icons";
import * as AdaptiveCards from "adaptivecards";
import { TimePicker } from "../time-picker/timepicker";
import {
  Button,
  Loader,
  Dropdown,
  Text,
  Flex,
  Input,
  TextArea,
  RadioGroup,
  Datepicker,
  DatepickerProps,
  Checkbox,
} from "@fluentui/react-northstar";
import * as microsoftTeams from "@microsoft/teams-js";
/* import { GraphFileBrowser } from '@microsoft/file-browser'; */

import "./newMessage.scss";
import "./teamTheme.scss";
import {
  getDraftNotification,
  getTeams,
  createDraftNotification,
  updateDraftNotification,
  searchGroups,
  getGroups,
  verifyGroupAccess,
  getUsers,
  searchUsers,
  postFileToBlob,
} from "../../apis/messageListApi";
import {
  getInitAdaptiveCard,
  setCardTitle,
  setCardImageLink,
  setCardSummary,
  setCardAuthor,
  setCardBtn,
} from "../AdaptiveCard/adaptiveCard";
import { getBaseUrl } from "../../configVariables";
import { ImageUtil } from "../../utility/imageutility";
import { TFunction } from "i18next";
import internal from "assert";

type dropdownItem = {
  key: string;
  header: string;
  content: string;
  image: string;
  team: {
    id: string;
  };
};

export interface simpleDropdown {
  key: string,
  header: string,

}

export interface IDraftMessage {
  id?: string;
  title: string;
  imageLink?: string;
  summary?: string;
  author: string;
  buttonTitle?: string;
  buttonLink?: string;
  station: string;
  teams: any[];
  rosters: any[];
  groups: any[];
  users: any[];
  allUsers: boolean;
  isRecurring: boolean;
  timeFrame: number;
  timePeriod: number;
  sendDate: Date;
}

export interface formState {
  title: string;
  summary?: string;
  btnLink?: string;
  imageLink?: string;
  btnTitle?: string;
  author: string;
  card?: any;
  page: string;
  station: string;
  isRecurring: boolean;
  selectedTimeFrame: simpleDropdown | null;
  selectedTimePeriod: simpleDropdown | null;
  sendDate: Date;
  teamsOptionSelected: boolean;
  rostersOptionSelected: boolean;
  allUsersOptionSelected: boolean;
  groupsOptionSelected: boolean;
  usersOptionSelected: boolean;
  teams?: any[];
  groups?: any[];
  users?: any[];
  exists?: boolean;
  messageId: string;
  loader: boolean;
  groupAccess: boolean;
  loading: boolean;
  noResultMessage: string;
  unstablePinned?: boolean;
  selectedTeamsNum: number;
  selectedRostersNum: number;
  selectedGroupsNum: number;
  selectedUsersNum: number;
  selectedRadioBtn: string;
  selectedTeams: dropdownItem[];
  selectedRosters: dropdownItem[];
  selectedGroups: dropdownItem[];
  selectedUsers: dropdownItem[];
  errorImageUrlMessage: string;
  errorButtonUrlMessage: string;
  errorFileUploadlMessage: string;
}

export interface INewMessageProps extends RouteComponentProps, WithTranslation {
  getDraftMessagesList?: any;
  getStation?: any;
}

class NewMessage extends React.Component<INewMessageProps, formState> {
  readonly localize: TFunction;
  private card: any;

  constructor(props: INewMessageProps) {
    super(props);
    initializeIcons();
    this.localize = this.props.t;
    this.card = getInitAdaptiveCard(this.localize);
    this.setDefaultCard(this.card);

    //We need to get the station value from the previous component.
    //We are appending the value through the URL, so we can capture it by search split (search param unfortunately wasn't working on UAT).
    //@ts-ignore
    let params = new URL(window.document.location);
    const currentStation = params.search.split("station=")[1];
    this.state = {
      title: "",
      summary: "",
      author: "",
      btnLink: "",
      imageLink: "",
      btnTitle: "View Document",
      card: this.card,
      page: "CardCreation",
      teamsOptionSelected: false,
      rostersOptionSelected: false,
      allUsersOptionSelected: false,
      groupsOptionSelected: false,
      usersOptionSelected: true,
      messageId: "",
      loader: true,
      station: currentStation,
      groupAccess: false,
      loading: false,
      noResultMessage: "",
      unstablePinned: true,
      selectedTeamsNum: 0,
      selectedRostersNum: 0,
      selectedGroupsNum: 0,
      selectedUsersNum: 0,
      selectedRadioBtn: "teams",
      selectedTeams: [],
      selectedRosters: [],
      selectedGroups: [],
      selectedUsers: [],
      errorImageUrlMessage: "",
      errorButtonUrlMessage: "",
      errorFileUploadlMessage: "",
      isRecurring: false,
      selectedTimeFrame: null,
      selectedTimePeriod: null,
      sendDate: new Date(),
    };
  }

  public async componentDidMount() {
    microsoftTeams.initialize();
    //- Handle the Esc key
    document.addEventListener("keydown", this.escFunction, false);
    let params = this.props.match.params;
    this.setGroupAccess();
    this.getTeamList().then(() => {
      if ("id" in params) {
        let id = params["id"];
        this.getItem(id).then(() => {
          const selectedTeams = this.makeDropdownItemList(
            this.state.selectedTeams,
            this.state.teams
          );
          const selectedRosters = this.makeDropdownItemList(
            this.state.selectedRosters,
            this.state.teams
          );


          this.setState({
            exists: true,
            messageId: id,
            selectedTeams: selectedTeams,
            selectedRosters: selectedRosters,
          });
        });
        this.getGroupData(id).then(() => {
          const selectedGroups = this.makeDropdownItems(this.state.groups);
          this.setState({
            selectedGroups: selectedGroups,
          });
        });
        this.getUserData(id).then(() => {
          const selectedUsers = this.makeDropdownItems(this.state.users);
          this.setState({
            selectedUsers: selectedUsers,
          });
        });
      } else {
        this.setState(
          {
            exists: false,
            loader: false,
          },
          () => {
            let adaptiveCard = new AdaptiveCards.AdaptiveCard();
            adaptiveCard.parse(this.state.card);
            let renderedCard = adaptiveCard.render();
            document
              .getElementsByClassName("adaptiveCardContainer")[0]
              .appendChild(renderedCard);
            if (this.state.btnLink) {
              let link = this.state.btnLink;
              adaptiveCard.onExecuteAction = function (action) {
                window.open(link, "_blank");
              };
            }
          }
        );
      }
    });
  }

  private makeDropdownItems = (items: any[] | undefined) => {
    const resultedTeams: dropdownItem[] = [];
    if (items) {
      items.forEach((element) => {
        resultedTeams.push({
          key: element.id,
          header: element.name,
          content: element.mail,
          image: ImageUtil.makeInitialImage(element.name),
          team: {
            id: element.id,
          },
        });
      });
    }
    return resultedTeams;
  };

  private makeDropdownItemList = (
    items: any[],
    fromItems: any[] | undefined
  ) => {
    const dropdownItemList: dropdownItem[] = [];
    items.forEach((element) =>
      dropdownItemList.push(
        typeof element !== "string"
          ? element
          : {
            key: fromItems!.find((x) => x.id === element).id,
            header: fromItems!.find((x) => x.id === element).name,
            image: ImageUtil.makeInitialImage(
              fromItems!.find((x) => x.id === element).name
            ),
            team: {
              id: element,
            },
          }
      )
    );
    return dropdownItemList;
  };

  public setDefaultCard = (card: any) => {
    const titleAsString = this.localize("TitleText");
    const summaryAsString = this.localize("Summary");
    const authorAsString = this.localize("Author1");
    const buttonTitleAsString = this.localize("ButtonTitle");

    setCardTitle(card, titleAsString);
    let imgUrl = getBaseUrl() + "/image/imagePlaceholder.png";
    setCardImageLink(card, imgUrl);
    setCardSummary(card, summaryAsString);
    setCardAuthor(card, authorAsString);
    setCardBtn(card, buttonTitleAsString, "https://adaptivecards.io");
  };

  private getTeamList = async () => {
    try {
      const response = await getTeams();
      this.setState({
        teams: response.data,
      });
    } catch (error) {
      return error;
    }
  };

  private getGroupItems() {
    if (this.state.groups) {
      return this.makeDropdownItems(this.state.groups);
    }
    const dropdownItems: dropdownItem[] = [];
    return dropdownItems;
  }
  private getUserItems() {
    if (this.state.users) {
      return this.makeDropdownItems(this.state.users);
    }
    const dropdownItems: dropdownItem[] = [];
    return dropdownItems;
  }


  private getTimePeriods(): simpleDropdown[] {
    return [
      {
        header: "Days",
        key: "0",
      },
      {
        header: "Weeks",
        key: "1",
      },
      {
        header: "Months",
        key: "2",
      },
    ];
  }


  private getTimeFrames(): simpleDropdown[] {
    return [
      {
        header: "0",
        key: '0',
      },
      {
        header: "1",
        key: '1',
      },
      {
        header: "2",
        key: '2',
      },
      {
        header: "3",
        key: '3',
      },
      {
        header: "4",
        key: '4',
      },
      {
        header: "5",
        key: '5',
      },
      {
        header: "6",
        key: '6',
      },
      {
        header: "7",
        key: '7',
      },
      {
        header: "8",
        key: '8',
      },
      {
        header: "9",
        key: '9',
      },
      {
        header: "10",
        key: '10',
      },
      {
        header: "11",
        key: '11',
      },
      {
        header: "12",
        key: '12',
      },
    ];
  }


  private setGroupAccess = async () => {
    await verifyGroupAccess()
      .then(() => {
        this.setState({
          groupAccess: true,
        });
      })
      .catch((error) => {
        const errorStatus = error.response.status;
        if (errorStatus === 403) {
          this.setState({
            groupAccess: false,
          });
        } else {
          throw error;
        }
      });
  };

  private getGroupData = async (id: number) => {
    try {
      const response = await getGroups(id);
      this.setState({
        groups: response.data,
      });
    } catch (error) {
      return error;
    }
  };
  private getUserData = async (id: number) => {
    try {
      const response = await getUsers(id);
      this.setState({
        users: response.data,
      });
    } catch (error) {
      return error;
    }
  };

  private getItem = async (id: number) => {
    try {
      const response = await getDraftNotification(id);
      const draftMessageDetail = response.data;
      let selectedRadioButton = "teams";
      if (draftMessageDetail.rosters.length > 0) {
        selectedRadioButton = "rosters";
      } else if (draftMessageDetail.groups.length > 0) {
        selectedRadioButton = "groups";
      } else if (draftMessageDetail.allUsers) {
        selectedRadioButton = "allUsers";
      } else if (draftMessageDetail.users) {
        selectedRadioButton = "users";
      }
      this.setState({
        teamsOptionSelected: draftMessageDetail.teams.length > 0,
        selectedTeamsNum: draftMessageDetail.teams.length,
        rostersOptionSelected: draftMessageDetail.rosters.length > 0,
        selectedRostersNum: draftMessageDetail.rosters.length,
        groupsOptionSelected: draftMessageDetail.groups.length > 0,
        selectedGroupsNum: draftMessageDetail.groups.length,
        selectedUsersNum: draftMessageDetail.users.legnth,
        usersOptionSelected: draftMessageDetail.users.length > 0,
        selectedRadioBtn: selectedRadioButton,
        selectedTeams: draftMessageDetail.teams,
        selectedRosters: draftMessageDetail.rosters,
        selectedGroups: draftMessageDetail.groups,
        selectedUsers: draftMessageDetail.users,
      });

      setCardTitle(this.card, draftMessageDetail.title);
      setCardImageLink(this.card, draftMessageDetail.imageLink);
      setCardSummary(this.card, draftMessageDetail.summary);
      setCardAuthor(this.card, draftMessageDetail.author);
      setCardBtn(
        this.card,
        draftMessageDetail.buttonTitle,
        draftMessageDetail.buttonLink
      );
console.log(draftMessageDetail)
console.log(draftMessageDetail.timePeriod.toString())
      const selectedTimeFrame = this.getTimeFrames().find(x => x.key === draftMessageDetail.timeFrame.toString())
      const selectedTimePeriod = this.getTimePeriods().find(x => x.key === draftMessageDetail.timePeriod.toString())
console.log(selectedTimePeriod)
      this.setState(
        {
          title: draftMessageDetail.title,
          summary: draftMessageDetail.summary,
          btnLink: draftMessageDetail.buttonLink,
          imageLink: draftMessageDetail.imageLink,
          btnTitle: draftMessageDetail.buttonTitle,
          station: draftMessageDetail.station,
          author: draftMessageDetail.author,
          loader: false,
          isRecurring: draftMessageDetail.isRecurring,
          sendDate: new Date(draftMessageDetail.sendDate),
          selectedTimeFrame: selectedTimeFrame,
          selectedTimePeriod: selectedTimePeriod,
        },
        () => {
          this.updateCard();
        }
      );
    } catch (error) {
      return error;
    }
  };
  private uploadFile = async (event: Event) => {
    let parent = event.target as HTMLInputElement;

    this.setState({
      errorFileUploadlMessage: "",
    });

    if (parent != null && parent.files != null) {
      let capUpload = true;

      var file = parent.files[0];

      if (
        file.type !== "application/pdf" &&
        file.type !== "image/jpeg" &&
        file.type !== "image/png"
      ) {
        capUpload = false;
        this.setState({
          errorFileUploadlMessage:
            "The File upload must be of type PDF or an JPG/PNG Format",
        });
      }

      if (capUpload) {
        let response = await postFileToBlob(parent.files);

        this.setState({
          btnLink: response.data[0],
        });
      }
    }
  };

  public componentWillUnmount() {
    document.removeEventListener("keydown", this.escFunction, false);
  }
  public render(): JSX.Element {
    if (this.state.loader) {
      return (
        <div className="Loader">
          <Loader />
        </div>
      );
    } else {
      if (this.state.page === "CardCreation") {
        return (
          <div className="taskModule">
            <Flex
              column
              className="formContainer"
              vAlign="stretch"
              gap="gap.small"
            >
              <Flex className="scrollableContent">
                <Flex.Item size="size.half">
                  <Flex column className="formContentContainer">
                    <Input
                      className="inputField"
                      value={this.state.title}
                      label={this.localize("TitleText")}
                      placeholder={this.localize("PlaceHolderTitle")}
                      onChange={this.onTitleChanged}
                      autoComplete="off"
                      fluid
                    />
                    <Input
                      fluid
                      className="inputField"
                      value={this.state.imageLink}
                      label={this.localize("ImageURL")}
                      placeholder={this.localize("ImageURL")}
                      onChange={this.onImageLinkChanged}
                      error={!(this.state.errorImageUrlMessage === "")}
                      autoComplete="off"
                    />
                    <Text
                      className={
                        this.state.errorImageUrlMessage === "" ? "hide" : "show"
                      }
                      error
                      size="small"
                      content={this.state.errorImageUrlMessage}
                    />

                    <div className="textArea">
                      <Text content={this.localize("Summary")} />
                      <TextArea
                        autoFocus
                        placeholder={this.localize("Summary")}
                        value={this.state.summary}
                        onChange={this.onSummaryChanged}
                        fluid
                      />
                    </div>

                    <Input
                      className="inputField"
                      value={this.state.author}
                      label={this.localize("Author")}
                      placeholder={this.localize("Author")}
                      onChange={this.onAuthorChanged}
                      autoComplete="off"
                      accept="application/pdf"
                      fluid
                    />
                    <Input
                      className="inputField"
                      fluid
                      disabled
                      value={this.state.btnTitle}
                      //label={this.localize("ButtonTitle")}
                      type="hidden"
                      placeholder={this.localize("ButtonTitle")}
                      onChange={this.onBtnTitleChanged}
                      autoComplete="off"
                    />
                    <Input
                      className="inputField"
                      fluid
                      value={this.state.btnLink}
                      placeholder={this.localize("DocumentURL")}
                      onChange={this.onBtnLinkChanged}
                      label={this.localize("DocumentURL")}
                      error={!(this.state.errorButtonUrlMessage === "")}
                      autoComplete="off"
                    />

                    <Flex
                      gap="gap.smaller"
                      className="input-label-margin-between"
                    >
                      <Flex.Item size="size.half">
                        <div>
                          <label>Send Date</label>
                          <Datepicker
                          
                            onDateChange={this.handleChange}
                            selectedDate={this.state.sendDate}
                          />
                        </div>
                      </Flex.Item>
                      <Flex.Item size="size.half">
                        <div>
                          <label>Send Time</label>
                          <TimePicker
                            hours={this.state.sendDate.getHours()}
                            minutes={this.state.sendDate.getMinutes()}
                            onPickerClose={this.onStartTimeChange}
                          />
                        </div>
                      </Flex.Item>
                    </Flex>
                    <Flex gap="gap.smaller">
                      <Flex.Item>
                        <Checkbox label="Is this a recurring message?" toggle checked={this.state.isRecurring} onChange={this.onCheckBoxChange} />
                      </Flex.Item>
                    </Flex>
                    {this.state.isRecurring ? 
                    <Flex
                      gap="gap.smaller"
                      className="input-label-margin-between"
                    >
                      <Flex.Item size="size.half">
                        <div>
                          <label>Repeat Time Frame</label>
                          <Dropdown
                            items={this.getTimeFrames()}
                            value={this.state.selectedTimeFrame}
                            onChange={this.onTimeFrameChange}
                            fluid
                          />
                        </div>
                      </Flex.Item>
                      <Flex.Item size="size.half">
                        <div>
                          <label>Repeat Time Period</label>
                          <Dropdown
                            items={this.getTimePeriods()}
                            value={this.state.selectedTimePeriod}
                            onChange={this.onTimePeriodChange}
                            fluid
                          />
                        </div>
                      </Flex.Item>
                    </Flex> : <></>}
                  </Flex>
                </Flex.Item>

                <Flex.Item size="size.half">
                  <div className="adaptiveCardContainer"></div>
                </Flex.Item>
              </Flex>

              <Flex className="footerContainer" vAlign="end" hAlign="end">
                <Flex className="buttonContainer">
                  <Button
                    content={this.localize("Next")}
                    disabled={this.isNextBtnDisabled()}
                    id="saveBtn"
                    onClick={this.onNext}
                    primary
                  />
                </Flex>
              </Flex>
            </Flex>
          </div>
        );
      } else if (this.state.page === "AudienceSelection") {
        return (
          <div className="taskModule">
            <Flex
              column
              className="formContainer"
              vAlign="stretch"
              gap="gap.small"
            >
              <Flex className="scrollableContent">
                <Flex.Item size="size.half">
                  <Flex column className="formContentContainer">
                    <h3>{this.localize("SendHeadingText")}</h3>
                    <RadioGroup
                      className="radioBtns"
                      checkedValue={this.state.selectedRadioBtn}
                      onCheckedValueChange={this.onGroupSelected}
                      vertical={true}
                      items={[
                        {
                          name: "users",
                          key: "users",
                          value: "users",
                          label: this.localize("SendToUsers"),
                          children: (Component, { name, ...props }) => {
                            return (
                              <Flex key={name} column>
                                <Component {...props} />
                                <Dropdown
                                  className="hideToggle"
                                  hidden={!this.state.usersOptionSelected}
                                  placeholder={this.localize(
                                    "SendToUsersPlaceHolder"
                                  )}
                                  search={this.onUserSearch}
                                  multiple
                                  loading={this.state.loading}
                                  loadingMessage={this.localize("LoadingText")}
                                  items={this.getUserItems()}
                                  value={this.state.selectedUsers}
                                  onSearchQueryChange={
                                    this.onUserSearchQueryChange
                                  }
                                  onChange={this.onUserChange}
                                  noResultsMessage={this.state.noResultMessage}
                                  unstable_pinned={this.state.unstablePinned}
                                />
                              </Flex>
                            );
                          },
                        },
                        {
                          name: "groups",
                          key: "groups",
                          value: "groups",
                          label: this.localize("SendToGroups"),
                          children: (Component, { name, ...props }) => {
                            return (
                              <Flex key={name} column>
                                <Component {...props} />

                                <Dropdown
                                  className="hideToggle"
                                  hidden={
                                    !this.state.groupsOptionSelected ||
                                    !this.state.groupAccess
                                  }
                                  placeholder={this.localize(
                                    "SendToGroupsPlaceHolder"
                                  )}
                                  search={this.onGroupSearch}
                                  multiple
                                  loading={this.state.loading}
                                  loadingMessage={this.localize("LoadingText")}
                                  items={this.getGroupItems()}
                                  value={this.state.selectedGroups}
                                  onSearchQueryChange={
                                    this.onGroupSearchQueryChange
                                  }
                                  onChange={this.onGroupsChange}
                                  noResultsMessage={this.state.noResultMessage}
                                  unstable_pinned={this.state.unstablePinned}
                                />
                              </Flex>
                            );
                          },
                        },
                      ]}
                    ></RadioGroup>
                  </Flex>
                </Flex.Item>
                <Flex.Item size="size.half">
                  <div className="adaptiveCardContainer"></div>
                </Flex.Item>
              </Flex>
              <Flex className="footerContainer" vAlign="end" hAlign="end">
                <Flex className="buttonContainer" gap="gap.small">
                  <Flex.Item push>
                    <Button
                      content={this.localize("Back")}
                      onClick={this.onBack}
                      secondary
                    />
                  </Flex.Item>
                  <Button
                    content={this.localize("SaveAsDraft")}
                    disabled={this.isSaveBtnDisabled()}
                    id="saveBtn"
                    onClick={this.onSave}
                    primary
                  />
                </Flex>
              </Flex>
            </Flex>
          </div>
        );
      } else {
        return <div>Error</div>;
      }
    }
  }
  private handleChange = (date: DatepickerProps, data: any) => {
    // Some custom components, liek Datepicker, can't raise native browser events
    // Since there is no event, we get the value from the second "data" object


    let inputDate =   data.value
    inputDate.setHours(this.state.sendDate.getHours(), this.state.sendDate.getMinutes())

    this.setState({
      sendDate: inputDate,
    });
    console.log(this.state.sendDate);
  };

  private onStartTimeChange = (hours: number, min: number) => {

    let date = this.state.sendDate;
    date.setHours(hours, min)
    this.setState({
      sendDate: new Date(date),
    });
  };

  private onGroupSelected = (event: any, data: any) => {
    this.setState({
      selectedRadioBtn: data.value,
      teamsOptionSelected: data.value === "teams",
      rostersOptionSelected: data.value === "rosters",
      groupsOptionSelected: data.value === "groups",
      usersOptionSelected: data.value === "users",
      allUsersOptionSelected: data.value === "allUsers",
      selectedTeams: data.value === "teams" ? this.state.selectedTeams : [],
      selectedTeamsNum:
        data.value === "teams" ? this.state.selectedTeamsNum : 0,
      selectedRosters:
        data.value === "rosters" ? this.state.selectedRosters : [],
      selectedRostersNum:
        data.value === "rosters" ? this.state.selectedRostersNum : 0,
      selectedGroups: data.value === "groups" ? this.state.selectedGroups : [],
      selectedGroupsNum:
        data.value === "groups" ? this.state.selectedGroupsNum : 0,
      selectedUsers: data.value === "users" ? this.state.selectedUsers : [],
      selectedUsersNum:
        data.value === "users" ? this.state.selectedUsersNum : 0,
    });
  };

  private isSaveBtnDisabled = () => {
    const groupsSelectionIsValid =
      (this.state.groupsOptionSelected && this.state.selectedGroupsNum !== 0) ||
      !this.state.groupsOptionSelected;
    const usersSelectionIsValid =
      (this.state.usersOptionSelected && this.state.selectedUsersNum !== 0) ||
      !this.state.usersOptionSelected;
    const nothingSelected =
      !this.state.groupsOptionSelected && !this.state.usersOptionSelected;
    // return (!teamsSelectionIsValid || !rostersSelectionIsValid || !groupsSelectionIsValid || !usersSelectionIsValid || nothingSelected)
    return !groupsSelectionIsValid || !usersSelectionIsValid || nothingSelected;
  };

  private isNextBtnDisabled = () => {
    const title = this.state.title;
    const btnTitle = this.state.btnTitle;
    const btnLink = this.state.btnLink;
    return !(
      title &&
      ((btnTitle && btnLink) || (!btnTitle && !btnLink)) &&
      this.state.errorImageUrlMessage === "" &&
      this.state.errorButtonUrlMessage === ""
    );
  };

  private getItems = () => {
    const resultedTeams: dropdownItem[] = [];
    if (this.state.teams) {
      let remainingUserTeams = this.state.teams;
      if (this.state.selectedRadioBtn !== "allUsers") {
        if (this.state.selectedRadioBtn === "teams") {
          this.state.teams.filter(
            (x) =>
              this.state.selectedTeams.findIndex((y) => y.team.id === x.id) < 0
          );
        } else if (this.state.selectedRadioBtn === "rosters") {
          this.state.teams.filter(
            (x) =>
              this.state.selectedRosters.findIndex((y) => y.team.id === x.id) <
              0
          );
        }
      }
      remainingUserTeams.forEach((element) => {
        resultedTeams.push({
          key: element.id,
          header: element.name,
          content: element.mail,
          image: ImageUtil.makeInitialImage(element.name),
          team: {
            id: element.id,
          },
        });
      });
    }
    return resultedTeams;
  };

  private static MAX_SELECTED_TEAMS_NUM: number = 20;

  private onTeamsChange = (event: any, itemsData: any) => {
    if (itemsData.value.length > NewMessage.MAX_SELECTED_TEAMS_NUM) return;
    this.setState({
      selectedTeams: itemsData.value,
      selectedTeamsNum: itemsData.value.length,
      selectedRosters: [],
      selectedRostersNum: 0,
      selectedGroups: [],
      selectedGroupsNum: 0,
      selectedUsers: [],
      selectedUsersNum: 0,
    });
  };

  private onRostersChange = (event: any, itemsData: any) => {
    if (itemsData.value.length > NewMessage.MAX_SELECTED_TEAMS_NUM) return;
    this.setState({
      selectedRosters: itemsData.value,
      selectedRostersNum: itemsData.value.length,
      selectedTeams: [],
      selectedTeamsNum: 0,
      selectedGroups: [],
      selectedGroupsNum: 0,
      selectedUsers: [],
      selectedUsersNum: 0,
    });
  };

  private onGroupsChange = (event: any, itemsData: any) => {
    this.setState({
      selectedGroups: itemsData.value,
      selectedGroupsNum: itemsData.value.length,
      groups: [],
      selectedTeams: [],
      selectedTeamsNum: 0,
      selectedRosters: [],
      selectedRostersNum: 0,
      selectedUsers: [],
      selectedUsersNum: 0,
    });
  };

  private onTimeFrameChange = (event: any, itemsData: any) => {
    this.setState({
      selectedTimeFrame: itemsData.value
    })
  }

  private onTimePeriodChange = (event: any, itemsData: any) => {
    console.log(itemsData)
    this.setState({
      selectedTimePeriod: itemsData.value
    })
  }

  private onCheckBoxChange = (event: any, data: any) => {
    console.log(data.checked)
    this.setState({
      isRecurring: data.checked
    })
    console.log(this.state.isRecurring)
  }
  private onUserChange = (event: any, itemsData: any) => {
    this.setState({
      selectedUsers: itemsData.value,
      selectedUsersNum: itemsData.value.length,
      selectedGroups: [],
      selectedGroupsNum: 0,
      groups: [],
      selectedTeams: [],
      selectedTeamsNum: 0,
      selectedRosters: [],
      selectedRostersNum: 0,
    });
  };

  private onGroupSearch = (itemList: any, searchQuery: string) => {
    const result = itemList.filter(
      (item: { header: string; content: string }) =>
        (item.header &&
          item.header.toLowerCase().indexOf(searchQuery.toLowerCase()) !==
          -1) ||
        (item.content &&
          item.content.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1)
    );
    return result;
  };
  private onUserSearch = (itemList: any, searchQuery: string) => {
    const result = itemList.filter(
      (item: { header: string; content: string }) =>
        (item.header &&
          item.header.toLowerCase().indexOf(searchQuery.toLowerCase()) !==
          -1) ||
        (item.content &&
          item.content.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1)
    );
    return result;
  };

  private onGroupSearchQueryChange = async (event: any, itemsData: any) => {
    if (!itemsData.searchQuery) {
      this.setState({
        groups: [],
        noResultMessage: "",
      });
    } else if (itemsData.searchQuery && itemsData.searchQuery.length <= 2) {
      this.setState({
        loading: false,
        noResultMessage: this.localize("NoMatchMessage"),
      });
    } else if (itemsData.searchQuery && itemsData.searchQuery.length > 2) {
      // handle event trigger on item select.
      const result =
        itemsData.items &&
        itemsData.items.find(
          (item: { header: string }) =>
            item.header.toLowerCase() === itemsData.searchQuery.toLowerCase()
        );
      if (result) {
        return;
      }

      this.setState({
        loading: true,
        noResultMessage: "",
      });

      try {
        const query = encodeURIComponent(itemsData.searchQuery);
        const response = await searchGroups(query);
        this.setState({
          groups: response.data,
          loading: false,
          noResultMessage: this.localize("NoMatchMessage"),
        });
      } catch (error) {
        return error;
      }
    }
  };
  private onUserSearchQueryChange = async (event: any, itemsData: any) => {
    if (!itemsData.searchQuery) {
      this.setState({
        users: [],
        noResultMessage: "",
      });
    } else if (itemsData.searchQuery && itemsData.searchQuery.length <= 2) {
      this.setState({
        loading: false,
        noResultMessage: this.localize("NoMatchMessage"),
      });
    } else if (itemsData.searchQuery && itemsData.searchQuery.length > 2) {
      // handle event trigger on item select.
      const result =
        itemsData.items &&
        itemsData.items.find(
          (item: { header: string }) =>
            item.header.toLowerCase() === itemsData.searchQuery.toLowerCase()
        );
      if (result) {
        return;
      }

      this.setState({
        loading: true,
        noResultMessage: "",
      });

      try {
        const query = encodeURIComponent(itemsData.searchQuery);
        const response = await searchUsers(query);
        this.setState({
          users: response.data,
          loading: false,
          noResultMessage: this.localize("NoMatchMessage"),
        });
      } catch (error) {
        return error;
      }
    }
  };
  private onSave = () => {
    const selectedTeams: string[] = [];
    const selctedRosters: string[] = [];
    const selectedGroups: string[] = [];
    const selectedUsers: string[] = [];
    this.state.selectedTeams.forEach((x) => selectedTeams.push(x.team.id));
    this.state.selectedRosters.forEach((x) => selctedRosters.push(x.team.id));
    this.state.selectedGroups.forEach((x) => selectedGroups.push(x.team.id));
    this.state.selectedUsers.forEach((x) => selectedUsers.push(x.team.id));
    const draftMessage: IDraftMessage = {
      id: this.state.messageId,
      title: this.state.title,
      imageLink: this.state.imageLink,
      summary: this.state.summary,
      author: this.state.author,
      buttonTitle: this.state.btnTitle,
      buttonLink: this.state.btnLink,
      station: this.state.station,
      teams: selectedTeams,
      rosters: selctedRosters,
      groups: selectedGroups,
      users: selectedUsers,
      allUsers: this.state.allUsersOptionSelected,
      isRecurring: this.state.isRecurring,
      sendDate: this.state.sendDate,
      timeFrame: this.state.selectedTimeFrame !== null ? parseInt(this.state.selectedTimeFrame.key) : 0,
      timePeriod: this.state.selectedTimePeriod !== null ? parseInt(this.state.selectedTimePeriod.key) : 0,
    };
    if (this.state.exists) {
      this.editDraftMessage(draftMessage).then(() => {
        microsoftTeams.tasks.submitTask();
      });
    } else {
      this.postDraftMessage(draftMessage).then(() => {
        microsoftTeams.tasks.submitTask();
      });
    }
  };

  private editDraftMessage = async (draftMessage: IDraftMessage) => {
    try {
      await updateDraftNotification(draftMessage);
    } catch (error) {
      return error;
    }
  };

  private postDraftMessage = async (draftMessage: IDraftMessage) => {
    try {
      await createDraftNotification(draftMessage);
    } catch (error) {
      throw error;
    }
  };

  public escFunction(event: any) {
    if (event.keyCode === 27 || event.key === "Escape") {
      microsoftTeams.tasks.submitTask();
    }
  }

  private onNext = (event: any) => {
    this.setState(
      {
        page: "AudienceSelection",
      },
      () => {
        this.updateCard();
      }
    );
  };

  private onBack = (event: any) => {
    this.setState(
      {
        page: "CardCreation",
      },
      () => {
        this.updateCard();
      }
    );
  };

  private onTitleChanged = (event: any) => {
    let showDefaultCard =
      !event.target.value &&
      !this.state.imageLink &&
      !this.state.summary &&
      !this.state.author &&
      !this.state.btnTitle &&
      !this.state.btnLink;
    setCardTitle(this.card, event.target.value);
    setCardImageLink(this.card, this.state.imageLink);
    setCardSummary(this.card, this.state.summary);
    setCardAuthor(this.card, this.state.author);
    setCardBtn(this.card, this.state.btnTitle, this.state.btnLink);
    this.setState(
      {
        title: event.target.value,
        card: this.card,
      },
      () => {
        if (showDefaultCard) {
          this.setDefaultCard(this.card);
        }
        this.updateCard();
      }
    );
  };

  private onImageLinkChanged = (event: any) => {
    let url = event.target.value.toLowerCase();
    if (
      !(
        url === "" ||
        url.startsWith("https://") ||
        url.startsWith("data:image/png;base64,") ||
        url.startsWith("data:image/jpeg;base64,") ||
        url.startsWith("data:image/gif;base64,")
      )
    ) {
      this.setState({
        errorImageUrlMessage: this.localize("ErrorURLMessage"),
      });
    } else {
      this.setState({
        errorImageUrlMessage: "",
      });
    }

    let showDefaultCard =
      !this.state.title &&
      !event.target.value &&
      !this.state.summary &&
      !this.state.author &&
      !this.state.btnTitle &&
      !this.state.btnLink;
    setCardTitle(this.card, this.state.title);
    setCardImageLink(this.card, event.target.value);
    setCardSummary(this.card, this.state.summary);
    setCardAuthor(this.card, this.state.author);
    setCardBtn(this.card, this.state.btnTitle, this.state.btnLink);
    this.setState(
      {
        imageLink: event.target.value,
        card: this.card,
      },
      () => {
        if (showDefaultCard) {
          this.setDefaultCard(this.card);
        }
        this.updateCard();
      }
    );
  };

  private onSummaryChanged = (event: any) => {
    let showDefaultCard =
      !this.state.title &&
      !this.state.imageLink &&
      !event.target.value &&
      !this.state.author &&
      !this.state.btnTitle &&
      !this.state.btnLink;
    setCardTitle(this.card, this.state.title);
    setCardImageLink(this.card, this.state.imageLink);
    setCardSummary(this.card, event.target.value);
    setCardAuthor(this.card, this.state.author);
    setCardBtn(this.card, this.state.btnTitle, this.state.btnLink);
    this.setState(
      {
        summary: event.target.value,
        card: this.card,
      },
      () => {
        if (showDefaultCard) {
          this.setDefaultCard(this.card);
        }
        this.updateCard();
      }
    );
  };

  private onAuthorChanged = (event: any) => {
    let showDefaultCard =
      !this.state.title &&
      !this.state.imageLink &&
      !this.state.summary &&
      !event.target.value &&
      !this.state.btnTitle &&
      !this.state.btnLink;
    setCardTitle(this.card, this.state.title);
    setCardImageLink(this.card, this.state.imageLink);
    setCardSummary(this.card, this.state.summary);
    setCardAuthor(this.card, event.target.value);
    setCardBtn(this.card, this.state.btnTitle, this.state.btnLink);
    this.setState(
      {
        author: event.target.value,
        card: this.card,
      },
      () => {
        if (showDefaultCard) {
          this.setDefaultCard(this.card);
        }
        this.updateCard();
      }
    );
  };

  private onBtnTitleChanged = (event: any) => {
    const showDefaultCard =
      !this.state.title &&
      !this.state.imageLink &&
      !this.state.summary &&
      !this.state.author &&
      !event.target.value &&
      !this.state.btnLink;
    setCardTitle(this.card, this.state.title);
    setCardImageLink(this.card, this.state.imageLink);
    setCardSummary(this.card, this.state.summary);
    setCardAuthor(this.card, this.state.author);
    if (event.target.value && this.state.btnLink) {
      setCardBtn(this.card, event.target.value, this.state.btnLink);
      this.setState(
        {
          btnTitle: event.target.value,
          card: this.card,
        },
        () => {
          if (showDefaultCard) {
            this.setDefaultCard(this.card);
          }
          this.updateCard();
        }
      );
    } else {
      delete this.card.actions;
      this.setState(
        {
          btnTitle: event.target.value,
        },
        () => {
          if (showDefaultCard) {
            this.setDefaultCard(this.card);
          }
          this.updateCard();
        }
      );
    }
  };

  private onBtnLinkChanged = (event: any) => {
    if (
      !(
        event.target.value === "" ||
        event.target.value.toLowerCase().startsWith("https://")
      )
    ) {
      this.setState({
        errorButtonUrlMessage: this.localize("ErrorURLMessage"),
      });
    } else {
      this.setState({
        errorButtonUrlMessage: "",
      });
    }

    const showDefaultCard =
      !this.state.title &&
      !this.state.imageLink &&
      !this.state.summary &&
      !this.state.author &&
      !this.state.btnTitle &&
      !event.target.value;
    setCardTitle(this.card, this.state.title);
    setCardSummary(this.card, this.state.summary);
    setCardAuthor(this.card, this.state.author);
    setCardImageLink(this.card, this.state.imageLink);
    if (this.state.btnTitle && event.target.value) {
      setCardBtn(this.card, this.state.btnTitle, event.target.value);
      this.setState(
        {
          btnLink: event.target.value,
          card: this.card,
        },
        () => {
          if (showDefaultCard) {
            this.setDefaultCard(this.card);
          }
          this.updateCard();
        }
      );
    } else {
      delete this.card.actions;
      this.setState(
        {
          btnLink: event.target.value,
        },
        () => {
          if (showDefaultCard) {
            this.setDefaultCard(this.card);
          }
          this.updateCard();
        }
      );
    }
  };

  private updateCard = () => {
    const adaptiveCard = new AdaptiveCards.AdaptiveCard();
    adaptiveCard.parse(this.state.card);
    const renderedCard = adaptiveCard.render();
    const container = document.getElementsByClassName(
      "adaptiveCardContainer"
    )[0].firstChild;
    if (container != null) {
      container.replaceWith(renderedCard);
    } else {
      document
        .getElementsByClassName("adaptiveCardContainer")[0]
        .appendChild(renderedCard);
    }
    const link = this.state.btnLink;
    adaptiveCard.onExecuteAction = function (action) {
      window.open(link, "_blank");
    };
  };
}

const newMessageWithTranslation = withTranslation()(NewMessage);
export default newMessageWithTranslation;
